import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../../store/sessionStore.js';
import { GRID_16, at } from '../../lib/layout.js';
import LiveClock from '../LiveClock.js';

const CELL = 'rounded-lg border border-gray-700 bg-gray-900 px-4 py-2 flex flex-col justify-center min-w-0';
const NEUTRAL_BG = '#1a1a2e';

/** Taglia carattere del nome macchina in base alla lunghezza, per restare leggibile senza tagliare (richiesta 30/07/2026). */
function machineNameSizeClass(name: string): string {
  if (name.length > 35) return 'text-base';
  if (name.length > 20) return 'text-lg';
  return 'text-2xl';
}

/**
 * Riga 1 del foglio INTERFACCIA: nome macchina | stato macchina attuale | nr. ordine | data e ora |
 * tasto impostazione, sulle stesse colonne dei riquadri sottostanti (vedi lib/layout.ts).
 * Dal 30/07/2026 (seconda call col cliente, vedi update/20260730/piano-modifiche.md):
 * - il nome macchina è la descrizione (`machineName`), non più il codice, e può andare a capo;
 * - il numero ordine è sempre visibile qui (prima stava solo nella schermata principale);
 * - lo sfondo esterno riprende il colore dello stato macchina corrente, ma solo sulla
 *   schermata principale (le pagine di dettaglio restano neutre per non affaticare la lettura).
 * Lo stato resta un indicatore di sola lettura: si cambia dai pulsanti FERMO/AVVIO.
 */
export default function PwaShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { machineName, isOnline, setOnline, currentStateLog, currentOrderCode } = useSessionStore();

  useEffect(() => {
    const onLine = () => setOnline(true);
    const offLine = () => setOnline(false);
    window.addEventListener('online', onLine);
    window.addEventListener('offline', offLine);
    return () => { window.removeEventListener('online', onLine); window.removeEventListener('offline', offLine); };
  }, [setOnline]);

  const onSettings = location.pathname.startsWith('/workstation/settings');
  const isMainScreen = location.pathname === '/workstation';
  const background = isMainScreen ? (currentStateLog?.machineState.color || NEUTRAL_BG) : NEUTRAL_BG;
  const machineLabel = machineName ?? t('shell.loading');

  return (
    <div className="flex flex-col h-dvh overflow-hidden transition-colors duration-500" style={{ background }}>
      <div className={`${GRID_16} px-3 pt-3 shrink-0`}>
        <div style={at(1, 5)} className={CELL}>
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wide">{t('shell.machine')}</span>
          <span className={`text-white font-bold ${machineNameSizeClass(machineLabel)} whitespace-normal break-words leading-tight`}>
            {machineLabel}
          </span>
        </div>

        <div style={at(7, 3)} className={CELL}>
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wide">{t('state.current')}</span>
          {currentStateLog ? (
            <span className="flex items-center gap-2 min-w-0">
              <span
                className="w-3.5 h-3.5 rounded-full shrink-0 ring-2 ring-white/20"
                style={{ backgroundColor: currentStateLog.machineState.color || '#9ca3af' }}
              />
              <span className="font-bold text-lg truncate text-white">
                {currentStateLog.machineState.valueLocale}
              </span>
            </span>
          ) : (
            <span className="text-gray-500 text-sm italic truncate">{t('state.none')}</span>
          )}
        </div>

        <div style={at(11, 3)} className={CELL}>
          <span className="text-gray-500 text-[11px] font-bold uppercase tracking-wide">{t('shell.order')}</span>
          <span className="text-white font-bold text-lg truncate">{currentOrderCode ?? '—'}</span>
        </div>

        <div style={at(14, 2)} className={CELL}>
          <span className="flex items-center gap-1.5 text-gray-500 text-[11px] font-bold uppercase tracking-wide">
            <span className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? 'bg-green-400' : 'bg-gray-500'}`} />
            {t('shell.dateTime')}
          </span>
          <LiveClock />
        </div>

        <button
          style={at(16, 1)}
          onClick={() => navigate(onSettings ? '/workstation' : '/workstation/settings')}
          className={`${CELL} items-center hover:bg-gray-800 active:bg-gray-700 transition-colors`}
        >
          <span className="text-2xl leading-none text-white">⚙</span>
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </div>
    </div>
  );
}
